import React, { useState, useRef, useLayoutEffect, useMemo } from "react";

import config from "./tailwind.json";
import "./tailwind.output.css";
import { getNearestValuesOf } from "./utils/tailwind";
import SpaceValuesRow from "./components/SpaceValuesRow";
import nearestColor from "nearest-color";
import Color from "color";
const findNearestColor = nearestColor.from(config.colors);

const findColor = (value) => {
  try {
    var color = Color(value);

    const rgb = color.rgb();
    const alpha = rgb.alpha();
    const red = parseInt(rgb.red() * alpha + 255 * (1 - alpha));
    const green = parseInt(rgb.green() * alpha + 255 * (1 - alpha));
    const blue = parseInt(rgb.blue() * alpha + 255 * (1 - alpha));

    const newColor = new Color(`rgb(${red}, ${green}, ${blue})`).hex();

    return findNearestColor(newColor);
  } catch (error) {
    // console.log(error)
    return false;
  }
};

const Box = (props) => {
  const { title, data, component: Component, prefix } = props;

  if (!data || !data.length) {
    return null;
  }
  return (
    <div className="bg-gray-800 shadow-lg rounded-lg">
      <div className="text-sm font-bold p-2 pb-0  text-gray-400">{title}</div>
      <div className="p-2 space-y-1">
        {data.map((value) => (
          <Component prefix={prefix} value={value} key={value.name} />
        ))}
      </div>
    </div>
  );
};

const ColorComponent = (props) => {
  const { value } = props;

  return (
    <div className="flex space-x-2 items-center ">
      <div
        className="w-8 h-8 rounded shadow "
        style={{ backgroundColor: value.value }}
      />
      <div className="leading-none text-sm">
        <div className="text-gray-400">{value.name}</div>
        <div className="text-gray-600">{value.value}</div>
      </div>
    </div>
  );
};
function App() {
  const [baseFontSize, setBaseFontSize] = useState(16);

  const [variable, setVariable] = useState("14px");

  const searchInput = useRef();

  const search = (variable) => {
    if (!variable || String(variable).trim(" ") === "") {
      return {};
    }

    return {
      margin: getNearestValuesOf(config.margin, variable, baseFontSize),
      padding: getNearestValuesOf(config.padding, variable, baseFontSize),
      space: getNearestValuesOf(config.space, variable, baseFontSize),
      width: getNearestValuesOf(config.width, variable, baseFontSize),
      height: getNearestValuesOf(config.height, variable, baseFontSize),
      maxWidth: getNearestValuesOf(config.maxWidth, variable, baseFontSize),
      gap: getNearestValuesOf(config.gap, variable, baseFontSize),
      fontSize: getNearestValuesOf(config.fontSize, variable, baseFontSize),
      color: findColor(variable),
    };
  };

  const data = search(variable);

  useLayoutEffect(() => {    
  
    searchInput.current.focus();    
    searchInput.current.select()
  }, []);

  const color = useMemo(() => {
    try {
      var color = Color(variable);
      const rgb = color.rgb();
      return `rgba(${rgb.red()}, ${rgb.green()}, ${rgb.blue()}, ${rgb.alpha()})`;
    } catch (error) {
      return false;
    }
  }, [variable]);
  return (
    <div className="w-full md:w-2/3 xl:max-w-4xl min-h-screen flex items-center flex-col p-4 mx-auto pt-24">
      <div className="text-center mb-10 text-gray-400">
        <div className="text-3xl">TailwindCSS Matcher</div>
        <div>
          Find the nearest value to your design{" "}
          <span role="img" aria-label="art">
            🎨
          </span>
        </div>
      </div>

      <div className="grid grid-cols-3 w-full gap-4">
        <div className="w-full col-span-2 relative">
          <label className="absolute left-2 text-xs text-gray-500 ml-4 mt-1">
            px value / color
          </label>
          <input
            ref={searchInput}
            onChange={(e) => setVariable(e.target.value)}
            placeholder="Value"
            value={variable}            
            className={`leading-tight p-4 pb-3 pt-5 bg-gray-800 shadow-xl w-64 text-xl  text-gray-400 focus:outline-none focus:shadow-outline rounded-lg  w-full shadow-outline ${
              color !== false ? "pl-10" : ""
            }`}
          />
          {color !== false && (
            <div
              className="w-4 h-4 absolute left-0 top-0 mt-6 transform translate-y-px ml-4 rounded"
              style={{ backgroundColor: `${color}` }}
            />
          )}
        </div>
        <div className=" relative">
          <label className="absolute left-2 text-xs text-gray-500 ml-4 mt-1">
            Base font size
          </label>
          <input
            type="number"
            value={baseFontSize}
            onChange={(e) => setBaseFontSize(parseInt(e.target.value))}
            placeholder="Base font size"
            className="focus:outline-none focus:shadow-outline leading-tight p-4 pb-3 pt-5 bg-gray-800 shadow-xl w-64 text-xl  text-gray-400 focus:outline-none focus:outlines-shadow-xl22 rounded-lg  w-full"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3  w-full gap-4 mt-4">
        {!!data.color && (
          <Box component={ColorComponent} title={"Color"} data={[data.color]} />
        )}
        {color === false && (
          <React.Fragment>
            <Box
              prefix="m"
              component={SpaceValuesRow}
              title={"Margin"}
              data={data.margin}
            />
            <Box
              prefix="p"
              component={SpaceValuesRow}
              title={"Padding"}
              data={data.padding}
            />
            <Box
              prefix="space-x"
              component={SpaceValuesRow}
              title={"Space Between"}
              data={data.space}
            />
            <Box
              prefix="w"
              component={SpaceValuesRow}
              title={"Width"}
              data={data.width}
            />
            <Box
              prefix="h"
              component={SpaceValuesRow}
              title={"Height"}
              data={data.height}
            />

            <Box
              prefix="max-w"
              component={SpaceValuesRow}
              title={"Max width"}
              data={data.maxWidth}
            />

            <Box
              prefix="gap"
              component={SpaceValuesRow}
              title={"Grid gap"}
              data={data.gap}
            />
            <Box
              prefix="text"
              component={SpaceValuesRow}
              title={"Font size"}
              data={data.fontSize}
            />
          </React.Fragment>
        )}
      </div>

      <div className="w-full h-px bg-gray-800 mt-5 mb-3" />

      <div className="text-gray-200" >
        By{" "}
        <a without rel="noopener noreferrer" href="https://twitter.com/SolaimanKmail" target="_blank" className="text-blue-500 hover:underline">
          @SolaimanKmail
        </a>
      </div>
    </div>
  );
}

export default App;
